import { AffiliateLandingInterface } from 'types/affiliate';
import Image from 'next/image';
import React, { useEffect, forwardRef, ReactNode, useRef, useState } from 'react';

declare let window: any;

const loadScript = (url: string, callback: () => void) => {
    if (document.getElementById('google-maps-script')) {
        callback();
        return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';
    script.src = url;

    script.onload = () => callback();

    document.head.appendChild(script);
};

interface Props {
    children?: ReactNode;
    onSearch?: any;
    setEnableSearch?: any;
    hasMRA?: boolean;
    affiliateByRef?: AffiliateLandingInterface
}

export type Ref = HTMLInputElement;

const allowedStates = ["MO", "KS"];

export const AutocompleteGoogle = forwardRef<Ref, Props>((props, ref) => {
    const isScriptLoaded = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [filteredResults, setFilteredResults] = useState([]);
    const autocompleteService = useRef<any>(null);
    const geocoder = useRef<any>(null);
    const [sessionToken, setSessionToken] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<any>(null);

    const sendKeyDown = () => {
        const searchInput = document.getElementById('location_search_input');
        if (!searchInput) return;
        searchInput.dispatchEvent(
            new KeyboardEvent('keydown', {
                key: 'ArrowDown',
                code: 'ArrowDown',
                which: 40,
                keyCode: 40,
            })
        );
    };

    const initializeGoolge = (
        autoCompleteRef: any,
        search: any
    ) => {
        if (!window.google || !window.google.maps || !window.google.maps.places)
            return;
        const value = new window.google.maps.places.AutocompleteSessionToken();
        setSessionToken(value);
        // Initialize Google Places Services
        if (!autocompleteService.current) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!geocoder.current) {
            geocoder.current = new window.google.maps.Geocoder();
        }
        autoCompleteRef?.current?.addEventListener(
            'keydown',
            (event: KeyboardEvent) => {
                if (event?.key === 'Enter') {
                    const pacSelectedItem: any = document.querySelector('.pac-item-selected');
                    if (pacSelectedItem) {
                        setInputValue(pacSelectedItem?.textContent);
                    } else {
                        sendKeyDown();
                    }
                    search();
                    event.stopPropagation();
                    event.preventDefault();
                }
            }
        );
    }
    const handleInputChange = (e: any) => {
        const value = e.target.value;
        setInputValue(value);
        if (value.length > 1) {
            let request: any = {
                sessionToken,
                input: value,
                componentRestrictions: { country: 'us' },
            }
            if (props?.affiliateByRef?.restriction_type === 'area') {
                request = {
                    ...request,
                    strictBounds: true,
                    locationRestriction: {
                        north: +props?.affiliateByRef.northeast_lat_restriction, // Northern latitude
                        south: +props?.affiliateByRef.southwest_lat_restriction,  // Southern latitude
                        east: +props?.affiliateByRef.northeast_long_restriction,  // Eastern longitude
                        west: +props?.affiliateByRef.southwest_long_restriction   // Western longitude
                    },
                    types: ["(regions)"]
                };
            }
            autocompleteService.current.getPlacePredictions(
                request,
                (predictions: any, status: any) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                        filterResults(predictions, value);
                        setIsOpen(true);
                    }
                }
            );
        } else {
            setFilteredResults([]); // Clear results if input is too short
        }
    };

    const filterResults = (predictions: any, value?: string) => {
        const resultPromises = predictions.map((prediction: any) => {
            return new Promise(resolve => {
                geocoder.current.geocode({ placeId: prediction.place_id }, (results: any, status: any) => {
                    if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
                        const addressComponents = results[0].address_components;
                        let stateRestriction;
                        if (props?.affiliateByRef) {
                            stateRestriction = JSON.parse(props?.affiliateByRef.state_restriction);
                        }
                        const state = addressComponents.find((component: any) => component.types.includes("administrative_area_level_1"))?.short_name;
                        if (props?.affiliateByRef?.restriction_type === 'area') {
                            resolve({ description: prediction.description, state });
                        }
                        if (stateRestriction.includes(state) && props?.affiliateByRef?.restriction_type === 'state') {
                            resolve({ description: prediction.description, state });
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            });
        });

        // Resolve all promises and filter out null values
        setFilteredResults([]);
        Promise.all(resultPromises).then((results: any) => {
            setFilteredResults(results?.filter((item: any) => item?.state && item?.description?.toLowerCase()?.includes(value?.toLowerCase())));
        });
    };
    const handleSelect = (result: any) => {
        setInputValue(result.description + ` (${result?.state})`);
        setFilteredResults([]);
        setTimeout(() => {
            props?.onSearch();
        }, 100);
    };

    const urlParams = new URLSearchParams(window.location.search);
    const location = urlParams.get('location');

    const onSearch = () => {
        props?.onSearch();
        setTimeout(() => {
            setIsOpen(false);
        }, 400);
    }
    useEffect(() => {
        let value = location || '';
        if (props?.affiliateByRef?.restriction_type === 'city') {
            value = props?.affiliateByRef.city_restriction;
        }
        setInputValue(value);
    }, [location, props?.affiliateByRef])

    useEffect(() => {
        if (
            !window.google &&
            !document.getElementById('google-maps-script') &&
            !isScriptLoaded.current
        ) {
            isScriptLoaded.current = true;
            props?.setEnableSearch(true);

            // Add Google Maps API script with &loading=async parameter
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}&libraries=places&loading=async`,
                () =>
                    initializeGoolge(ref, onSearch)
            );
        } else if (window.google) {
            initializeGoolge(ref, onSearch);
        }
    }, [props?.onSearch, props?.setEnableSearch, ref]);

    useEffect(() => {
        // Function to close dropdown if clicked outside
        const handleClickOutside = (event: any) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        // Add event listener
        document.addEventListener("mousedown", handleClickOutside);

        // Cleanup event listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="search-location-input w-full relative">
            <label
                htmlFor="city_state"
                aria-label="city state"
                className="hidden"
            >
                City state:
            </label>
            <input
                id="location_search_input"
                aria-label="City state"
                name="city_state"
                ref={ref}
                type="search"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={props?.hasMRA ? "City, State" : "City, state, or zip code"}
                autoComplete="off"
                onBlur={() => setTimeout(() => setIsOpen(false), 400)}
            />
            {filteredResults.length > 0 && isOpen && (
                <div className='autoCompleteAuto' ref={dropdownRef}>
                    {filteredResults.map((result: any, index: number) => (
                        <div
                            key={'google_item_' + result.state}
                            className={`flex items-center px-[33px] py-[5px] border-t border-[#e6e6e6] cursor-pointer hover:bg-[#fafafa] ${index == 0 ? 'pac-item-selected' : ''}`}
                            onClick={() => handleSelect(result)}
                        >
                            <span className='pac-icon pac-icon-marker'></span>
                            {result.description} ({result.state})
                        </div>
                    ))}
                    <div className='flex justify-end p-4 pt-0'>
                        <Image src="/powered-by-google-on-white3.png" alt="" width="120" height="10" />
                    </div>
                </div>
            )}
        </div>
    );
});
